import { getFirestore, collection, query, orderBy, limit, getDocs, where } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { app } from "../firebase-config.js";

const db = getFirestore(app);
const auth = getAuth(app);

export const AdminOverviewService = {
    /**
     * Fetch all data required for the Overview Dashboard
     */
    async fetchOverviewData() {
        const todayStr = new Date().toISOString().split('T')[0];

        const [
            usersSnap,
            seriesSnap,
            hourlySnap,
            plazaSnap,
            dailyStatsSnap
        ] = await Promise.all([
            getDocs(query(collection(db, 'users'), orderBy('createdAt', 'desc'), limit(1000))),
            getDocs(query(collection(db, 'series'), limit(500))),
            getDocs(query(collection(db, 'analytics_hourly_views'), orderBy('createdAt', 'desc'), limit(100))),
            getDocs(query(collection(db, 'plaza_feeds'), orderBy('createdAt', 'desc'), limit(20))),
            getDocs(query(collection(db, 'daily_stats'), orderBy('date', 'desc'), limit(1000)))
        ]);

        // Process all metrics
        const seriesMetrics = this._processSeriesMetrics(seriesSnap);
        const userMetrics = this._processUserMetrics(usersSnap, todayStr);
        const velocityList = this._processContentVelocity(hourlySnap);
        const pulseItems = this._processPulse(usersSnap, plazaSnap);
        const chartData = this._processChartData(dailyStatsSnap, usersSnap);
        const localeStats = await this._fetchLocaleStats(); // New

        return {
            totalUsers: usersSnap.size,
            totalSeries: seriesSnap.size,
            seriesMetrics,
            userMetrics,
            velocityList,
            pulseItems,
            chartData,
            localeStats
        };
    },

    // New Helper: Fetch Daily Locale Stats
    async _fetchLocaleStats() {
        try {
            const today = new Date().toISOString().split('T')[0];
            // Query docs starting with today's date prefix (e.g. "2026-02-14_")
            // Firestore doesn't support "startsWith" in keys easily without a range query on __name__
            // Or we just store a 'date' field and query by that.
            // In auth.js we saved: { date: today, lang: lang, ... }
            // So we can query collection 'stats_daily_locale' where date == today.

            const q = query(
                collection(db, 'stats_daily_locale'),
                where('date', '==', today)
            );
            const snap = await getDocs(q);

            if (snap.empty) return [];

            const stats = snap.docs.map(d => ({
                lang: d.data().lang,
                count: (d.data().signup_count || 0) + (d.data().login_count || 0)
            }));

            // Sort by count desc
            return stats.sort((a, b) => b.count - a.count);

        } catch (e) {
            console.warn("Locale Stats Fetch Error:", e);
            return [];
        }
    },

    // --- Private Helper Methods ---

    _processSeriesMetrics(snapshot) {
        let totalViews = 0;
        let riskCount = 0;
        let topRiskTitle = "";
        let aiCount = 0;
        let adultCount = 0;
        let totalRatingSum = 0;
        let ratedSeriesCount = 0;

        snapshot.docs.forEach(doc => {
            const data = doc.data();
            const v1 = data.stats?.viewCount;
            const v2 = data.viewCount;
            const views = Number(v1 ?? v2 ?? 0);
            totalViews += views;

            // Risk Logic
            const completions = data.stats?.completions || 0;
            if (views > 100 && (completions / views) < 0.2) {
                riskCount++;
                if (!topRiskTitle) topRiskTitle = data.title;
            }

            // AI & Rating
            if (data.isAI) aiCount++;
            if (data.isAdult) adultCount++;
            if (data.stats?.averageRating > 0) {
                totalRatingSum += data.stats.averageRating;
                ratedSeriesCount++;
            }
        });

        const aiPercentage = snapshot.size > 0 ? Math.round((aiCount / snapshot.size) * 100) : 0;
        const globalAvgRating = ratedSeriesCount > 0 ? (totalRatingSum / ratedSeriesCount).toFixed(1) : "0.0";

        // Generate top 5 series for scroll depth heatmap (using completion rate as proxy)
        const topSeries = snapshot.docs
            .map(doc => {
                const d = doc.data();
                const views = d.stats?.viewCount || d.viewCount || 0;
                const completions = d.stats?.completions || 0;
                return {
                    title: d.title,
                    completionRate: views > 0 ? (completions / views) : 0
                };
            })
            .sort((a, b) => b.completionRate - a.completionRate)
            .slice(0, 5);

        return {
            totalViews,
            riskCount,
            topRiskTitle,
            aiPercentage,
            aiCount,
            adultCount,
            globalAvgRating,
            topSeries
        };
    },

    _processUserMetrics(snapshot, todayStr) {
        let pendingCount = 0;
        let returningUsers = 0;
        let loyalUsers = 0;
        let dau = 0;

        const currentUser = auth.currentUser;

        snapshot.docs.forEach(doc => {
            const d = doc.data();
            const uid = doc.id;

            if (d.authorStatus === 'pending') pendingCount++;
            if (d.visitCount > 1) returningUsers++;
            if (d.visitCount > 5) loyalUsers++;

            // DAU Logic
            const isToday = d.lastAttendanceDate === todayStr;
            let isLoginToday = false;
            if (!isToday && d.lastLogin && d.lastLogin.toDate) {
                try {
                    const loginDate = d.lastLogin.toDate().toISOString().split('T')[0];
                    if (loginDate === todayStr) isLoginToday = true;
                } catch (e) { }
            }

            // Force count current user if logic missed (fallback)
            const isCurrentUser = currentUser && (currentUser.uid === uid || currentUser.email === d.email);

            if (isToday || isLoginToday || isCurrentUser) {
                dau++;
            }
        });

        // Ensure DAU is at least 1 if user is logged in
        if (dau === 0 && currentUser) {
            dau = 1;
        }

        const totalUsers = snapshot.size;
        const returnRate = totalUsers > 0 ? Math.round((returningUsers / totalUsers) * 100) : 0;
        const loyalRate = totalUsers > 0 ? Math.round((loyalUsers / totalUsers) * 100) : 0;

        return {
            pendingCount,
            returningUsers,
            loyalUsers,
            dau,
            returnRate,
            loyalRate
        };
    },

    _processContentVelocity(snapshot) {
        const velocityMap = {};
        snapshot.docs.forEach(d => {
            const data = d.data();
            if (data.seriesTitle) {
                if (!velocityMap[data.seriesTitle]) velocityMap[data.seriesTitle] = 0;
                velocityMap[data.seriesTitle] += data.count;
            }
        });
        return Object.entries(velocityMap).sort((a, b) => b[1] - a[1]).slice(0, 3);
    },

    _processPulse(usersSnap, plazaSnap) {
        const pulseItems = [];
        // Recent Users (last 5 from already sorted query)
        usersSnap.docs.slice(0, 5).forEach(d => {
            pulseItems.push({
                type: 'user',
                text: `<span class="font-bold text-indigo-400">${d.data().displayName || 'User'}</span>님이 가입했습니다.`,
                time: d.data().createdAt?.toDate ? d.data().createdAt.toDate() : new Date()
            });
        });

        // Recent Posts
        plazaSnap.docs.slice(0, 5).forEach(d => {
            pulseItems.push({
                type: 'post',
                text: `새로운 포스트가 올라왔습니다: <span class="font-bold text-gray-300">"${d.data().content?.substring(0, 15)}..."</span>`,
                time: d.data().createdAt?.toDate ? d.data().createdAt.toDate() : new Date()
            });
        });

        return pulseItems.sort((a, b) => b.time - a.time);
    },

    _processChartData(dailyStatsSnap, usersSnap) {
        const generateDates = (days) => {
            const dates = [];
            for (let i = days - 1; i >= 0; i--) {
                const d = new Date();
                d.setDate(d.getDate() - i);
                dates.push(d.toISOString().split('T')[0]);
            }
            return dates;
        };
        const chartLabels = generateDates(14);

        // Daily Views
        const viewCounts = {};
        dailyStatsSnap.docs.forEach(doc => {
            const d = doc.data();
            if (d.date && chartLabels.includes(d.date)) {
                viewCounts[d.date] = (viewCounts[d.date] || 0) + (d.viewCount || 0);
            }
        });
        const viewData = chartLabels.map(date => viewCounts[date] || 0);

        // New Users Trend
        const userCounts = {};
        usersSnap.docs.forEach(doc => {
            const d = doc.data();
            if (d.createdAt && d.createdAt.toDate) {
                const date = d.createdAt.toDate().toISOString().split('T')[0];
                if (chartLabels.includes(date)) {
                    userCounts[date] = (userCounts[date] || 0) + 1;
                }
            }
        });
        const userData = chartLabels.map(date => userCounts[date] || 0);

        return {
            labels: chartLabels,
            viewData,
            userData
        };
    }
};
